import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "nodePageParams";
const DEFAULT_PARAMS = Object.freeze({
  page: 1,
  offset: 0,
  limit: 10,
  filter: [],
  order: "",
  sort: "",
  dir: "",
  uuid: null,
});

const NodePageContext = createContext();

//* ---------------------------- Hook personalizzato ----------------------------
const usePersistedState = (key, defaultValue, skipSave = false) => {
  const [state, setState] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.uuid === defaultValue.uuid) {
          return { ...defaultValue, ...parsed };
        }
        return defaultValue;
      }
    } catch (error) {
      console.warn(`Failed to parse localStorage key "${key}":`, error);
    }
    return defaultValue;
  });

  const lastSavedValue = useRef(state);
  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    //* se skipSave è true, non scrive nulla
    if (skipSave) return;

    if (JSON.stringify(state) === JSON.stringify(lastSavedValue.current)) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify(state));
        lastSavedValue.current = state;
      } catch (error) {
        console.warn(`Failed to save to localStorage key "${key}":`, error);
      }
    }, 100);
  }, [state, key, skipSave]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  return [state, setState];
};

//* ---------------------------- Gestione parametri URL ----------------------------
const useUrlParams = () => {
  const { search } = useLocation();
  return useMemo(() => {
    const params = new URLSearchParams(search);
    return {
      uuid: params.get("uuid"),
    };
  }, [search]);
};

//* ---------------------------- Hook pubblico ----------------------------
export const useNodePage = () => {
  const context = useContext(NodePageContext);
  if (!context) {
    throw new Error("useNodePage must be used within a NodePageProvider");
  }
  return context;
};

//* ---------------------------- Provider principale ----------------------------
export const NodePageProvider = ({ children }) => {
  const urlParams = useUrlParams();
  const [skipSave, setSkipSave] = useState(false);

  const initialParams = useMemo(
    () => ({
      ...DEFAULT_PARAMS,
      uuid: urlParams.uuid,
    }),
    [urlParams.uuid],
  );

  const [params, setParams] = usePersistedState(
    STORAGE_KEY,
    initialParams,
    skipSave,
  );

  //* -------------------- Reset automatico al cambio UUID --------------------
  const lastUuidRef = useRef(urlParams.uuid);
  useEffect(() => {
    if (lastUuidRef.current === urlParams.uuid) return;
    lastUuidRef.current = urlParams.uuid;

    setSkipSave(true);

    //* Cancella localStorage e resetta stato
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn("⚠️ Errore nella rimozione localStorage:", e);
    }

    setParams({
      ...DEFAULT_PARAMS,
      uuid: urlParams.uuid,
    });

    //* riattiva il salvataggio dopo un breve timeout
    const t = setTimeout(() => setSkipSave(false), 300);
    return () => clearTimeout(t);
  }, [urlParams.uuid, setParams]);

  //* -------------------- Funzioni helper --------------------
  const updateParams = useCallback(
    (updates) => {
      if (!updates || typeof updates !== "object") return;

      setParams((prev) => {
        const newState = { ...prev, ...updates };

        //* validazioni base
        if (
          updates.page !== undefined &&
          (updates.page < 1 || !Number.isInteger(updates.page))
        ) {
          newState.page = prev.page;
        }

        if (
          updates.limit !== undefined &&
          (updates.limit < 1 || !Number.isInteger(updates.limit))
        ) {
          newState.limit = prev.limit;
        }

        if (
          updates.offset !== undefined &&
          (updates.offset < 0 || !Number.isInteger(updates.offset))
        ) {
          newState.offset = prev.offset;
        }

        return newState;
      });
    },
    [setParams],
  );

  const resetParams = useCallback(() => {
    setParams({
      ...DEFAULT_PARAMS,
      uuid: urlParams.uuid,
    });
  }, [setParams, urlParams.uuid]);

  //* -------------------- Memo del valore di contesto --------------------
  const contextValue = useMemo(
    () => ({
      newParams: params,
      setNewParams: updateParams,
      resetParams,
      isFirstPage: params.page === 1,
      hasFilters:
        params.filter &&
        params.filter.length > 0 &&
        !(params.filter.length === 1 && params.filter[0]["*"] === ""),
      currentOffset: (params.page - 1) * params.limit,
      STORAGE_KEY,
    }),
    [params, updateParams, resetParams],
  );

  return (
    <NodePageContext.Provider value={contextValue}>
      {children}
    </NodePageContext.Provider>
  );
};

//* ---------------------------- HOC di comodità ----------------------------
export const withNodePage = (Component) => {
  const WrappedComponent = (props) => {
    const nodePageContext = useNodePage();
    return <Component {...props} {...nodePageContext} />;
  };
  WrappedComponent.displayName = `withNodePage(${
    Component.displayName || Component.name
  })`;
  return WrappedComponent;
};
